"use client";
import { jsx, jsxs } from "react/jsx-runtime";
import { useCallback, useEffect } from "react";
import { cn, getHeaderColumnCount } from "../../../utils/index.js";
import { buttonVariants } from "../../Button/Button.variants.js";
import { Chevron } from "../../SVG/Chevron/index.js";
import { cleanTableProps } from "../cleanTableProps.js";
import { useTableContext } from "../Table/index.js";
import { useTableBodyContext } from "../TBody/index.js";
import { Td } from "../Td/index.js";
import { useTableHeadContext } from "../THead/index.js";
import { useTableRowDrawerContext } from "../TrDrawer/index.js";
import { useTableRowExpandableContext } from "../TrExpandable/index.js";
import { TableRowContext } from "./Tr.context.js";
import { trVariants } from "./Tr.variants.js";
function Tr({ children, className, ...props }) {
    const { striped: tableStriped, bordered: tableBordered, headerSticky: tableHeaderSticky, highlightOnHover: tableHighlightOnHover, placeholder, maxColumns, setMaxColumns } = useTableContext();
    const { striped: tableBodyStriped, highlightOnHover: tableBodyHighlightOnHover } = useTableBodyContext();
    const { sticky: tableHeadSticky, isHeaderRow } = useTableHeadContext();
    const { isExpandable, isExpanded, setIsExpanded } = useTableRowExpandableContext();
    const { isDrawer, isOpen, setIsOpen } = useTableRowDrawerContext();
    const { striped: tableRowStriped, isGroupHeader, highlightOnHover: tableRowHighlightOnHover } = props;
    const striped = !isGroupHeader && !isHeaderRow && (tableRowStriped ?? tableBodyStriped ?? tableStriped);
    const highlightOnHover = tableRowHighlightOnHover ?? tableBodyHighlightOnHover ?? tableHighlightOnHover ?? true;
    const sticky = isHeaderRow && (tableHeadSticky ?? tableHeaderSticky);
    const classes = cn(trVariants({
        ...props,
        striped,
        tableBordered,
        sticky,
        isHeaderRow,
        highlightOnHover
    }), className);
    const cleanedProps = cleanTableProps(props);
    const onOpenClick = useCallback(()=>{
        if (isExpandable) setIsExpanded(!isExpanded);
        if (isDrawer) setIsOpen(!isOpen);
    }, [
        isExpanded,
        isExpandable,
        isDrawer,
        isOpen,
        setIsExpanded,
        setIsOpen
    ]);
    useEffect(()=>{
        if (isHeaderRow) {
            let newMaxColumns = getHeaderColumnCount(children);
            if (placeholder) newMaxColumns++;
            if (newMaxColumns > maxColumns) setMaxColumns(newMaxColumns);
        }
    }, [
        placeholder,
        maxColumns,
        isHeaderRow,
        children,
        setMaxColumns
    ]);
    const { buttonSlot } = buttonVariants({});
    return /*#__PURE__*/ jsx(TableRowContext.Provider, {
        value: props,
        children: /*#__PURE__*/ jsxs("tr", {
            className: classes,
            ...cleanedProps,
            children: [
                (isExpandable || isDrawer) && /*#__PURE__*/ jsx(Td, {
                    className: "w-11 py-0 hover:cursor-pointer",
                    contentClassName: "m-w-11",
                    onClick: onOpenClick,
                    "aria-label": isExpandable ? 'Row expand or collapse' : 'Drawer open or close',
                    children: /*#__PURE__*/ jsx("button", {
                        className: cn(buttonSlot(), 'bg-transparent p-2'),
                        children: /*#__PURE__*/ jsx(Chevron, {
                            className: "p-0 text-primary-8",
                            isOpen: Boolean(isExpanded || isOpen)
                        })
                    })
                }),
                placeholder && !(isExpandable || isDrawer) && /*#__PURE__*/ jsx(Td, {
                    placeholder: true
                }),
                children
            ]
        })
    });
}
export { Tr };
